import { apiClient } from './client'
import type { ApiResponse } from '../types/api'
import type {
  AlgorithmAuditRow,
  Boost,
  BoostList,
  CollectedData,
  CollectedRecentData,
  CollectedWindowDays,
  ConsentData,
  CreateBoostInput,
  CreatorPreview,
  SeeData,
  SeeWindowDays,
  SessionsData,
  SessionWindowDays,
} from '../types/algorithm'

export const algorithmApi = {
  /** Whether this admin is on ALGORITHM_ALLOWED_EMAILS — decides if the menu shows the page. */
  checkAccess: () => apiClient.get<ApiResponse<{ allowed: boolean }>>('/admin/algorithm/access'),

  /** Whether this admin currently has an unlocked session (see `unlock`). */
  checkSession: () => apiClient.get<ApiResponse<{ unlocked: boolean }>>('/admin/algorithm/session'),

  /** Verifies a fresh 2FA code and opens a 30-minute sliding session. */
  unlock: (data: { code: string }) => apiClient.post<ApiResponse<void>>('/admin/algorithm/unlock', data),

  getSee: (params: { days: SeeWindowDays; includeSynthetic: boolean }) =>
    apiClient.get<ApiResponse<SeeData>>('/admin/algorithm/see', { params }),

  getConsent: () => apiClient.get<ApiResponse<ConsentData>>('/admin/algorithm/consent'),

  getSessions: (params: { days: SessionWindowDays }) =>
    apiClient.get<ApiResponse<SessionsData>>('/admin/algorithm/sessions', { params }),

  /** Everything the app reports, from the nightly summaries (complete EAT days). */
  getCollected: (params: { days: CollectedWindowDays }) =>
    apiClient.get<ApiResponse<CollectedData>>('/admin/algorithm/collected', { params }),

  /** Raw events received in the last 24 hours. */
  getCollectedRecent: () => apiClient.get<ApiResponse<CollectedRecentData>>('/admin/algorithm/collected/recent'),

  listBoosts: () => apiClient.get<ApiResponse<BoostList>>('/admin/algorithm/boosts'),

  /** Each change needs a fresh authenticator code. */
  createBoost: (data: CreateBoostInput) => apiClient.post<ApiResponse<Boost>>('/admin/algorithm/boosts', data),

  stopBoost: (id: string, data: { code: string }) =>
    apiClient.post<ApiResponse<Boost>>(`/admin/algorithm/boosts/${id}/stop`, data),

  extendBoost: (id: string, data: { code: string; endsAt: string }) =>
    apiClient.post<ApiResponse<Boost>>(`/admin/algorithm/boosts/${id}/extend`, data),

  searchCreators: (q: string) =>
    apiClient.get<ApiResponse<CreatorPreview[]>>('/admin/algorithm/creator-search', { params: { q } }),

  listAudit: () => apiClient.get<ApiResponse<AlgorithmAuditRow[]>>('/admin/algorithm/audit'),
}
